import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Add as AddIcon, DeleteOutline as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';

const TEAL = '#0d9488';
const TEAL_DARK = '#0f766e';

const thSx = {
  fontWeight: 700,
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: '#64748b',
  bgcolor: '#f8fafc',
  borderBottom: '1px solid #e2e8f0',
  py: 1.25
};

function formatDisplayDate(dateStr) {
  const d = new Date(`${String(dateStr).slice(0, 10)}T12:00:00`);
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

export default function StaffingPublicHolidaysTab({ apiRequest, onFeedback }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ id: null, holiday_date: '', name: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiRequest(`/api/company/hrms/holidays?year=${year}`);
      setHolidays(data.holidays || []);
    } catch (e) {
      onFeedback(e.message);
      setHolidays([]);
    } finally {
      setLoading(false);
    }
  }, [apiRequest, year, onFeedback]);

  useEffect(() => {
    load();
  }, [load]);

  const sorted = useMemo(
    () => [...holidays].sort((a, b) => String(a.holiday_date).localeCompare(String(b.holiday_date))),
    [holidays]
  );

  const openNew = () => {
    setForm({ id: null, holiday_date: `${year}-01-01`, name: '' });
    setDialogOpen(true);
  };

  const openEdit = (h) => {
    setForm({
      id: h.id,
      holiday_date: String(h.holiday_date).slice(0, 10),
      name: h.name || ''
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.holiday_date || !form.name.trim()) {
      onFeedback('Date and holiday name are required.');
      return;
    }
    setSaving(true);
    try {
      if (form.id) {
        await apiRequest(`/api/company/hrms/holidays/${form.id}`, {
          method: 'PUT',
          body: JSON.stringify({ holiday_date: form.holiday_date, name: form.name.trim() })
        });
        onFeedback('Holiday updated.');
      } else {
        await apiRequest('/api/company/hrms/holidays', {
          method: 'POST',
          body: JSON.stringify({ holiday_date: form.holiday_date, name: form.name.trim() })
        });
        onFeedback('Public holiday added. These days count as paid days in payroll (even without check-in).');
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      onFeedback(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this public holiday?')) return;
    try {
      await apiRequest(`/api/company/hrms/holidays/${id}`, { method: 'DELETE' });
      onFeedback('Holiday removed.');
      await load();
    } catch (e) {
      onFeedback(e.message);
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={1}>
        <Box>
          <Typography variant="h6" fontWeight={800} color="#0f172a">
            Public holidays
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add company holidays for the year. Employees are paid for these days without needing to check in.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            type="number"
            label="Year"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            sx={{ width: 100 }}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew} sx={{ bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK } }}>
            Add holiday
          </Button>
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ borderRadius: 2 }}>
        Public holidays are included in <strong>paid days</strong> for payroll and shown on the employee holiday calendar
        (distinct from check-in days). Employees see holidays vs attendance in their app.
      </Alert>

      <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <CardContent sx={{ py: 2 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Chip label={`${sorted.length} holiday(s) in ${year}`} size="small" sx={{ bgcolor: '#ecfdf5', color: TEAL_DARK, fontWeight: 600 }} />
          </Stack>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>Date</TableCell>
                  <TableCell sx={thSx}>Day</TableCell>
                  <TableCell sx={thSx}>Holiday name</TableCell>
                  <TableCell sx={thSx} align="right" width={100}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">Loading…</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && sorted.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary" gutterBottom>
                        No public holidays for {year}
                      </Typography>
                      <Button size="small" variant="outlined" onClick={openNew}>
                        Add first holiday
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  sorted.map((h) => {
                    const d = String(h.holiday_date).slice(0, 10);
                    return (
                      <TableRow key={h.id} hover>
                        <TableCell>
                          <Typography fontWeight={600}>{d}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDisplayDate(d).split(',')[0]}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={h.name} sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600 }} />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(h)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => remove(h.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{form.id ? 'Edit public holiday' : 'Add public holiday'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Date"
              InputLabelProps={{ shrink: true }}
              value={form.holiday_date}
              onChange={(e) => setForm((p) => ({ ...p, holiday_date: e.target.value }))}
            />
            <TextField
              fullWidth
              size="small"
              label="Holiday name"
              placeholder="e.g. Republic Day, Diwali"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving} sx={{ bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK } }}>
            {form.id ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
